import React from 'react';
import { Users, Crown, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TeamSwitcher from './TeamSwitcher';

interface HeaderProps {
  teamName?: string;
  teamId?: string;
  onLogout?: () => void;
  onTeamClick?: () => void;
  showTeamSwitcher?: boolean;
  onUpgrade?: () => void;
  showUpgrade?: boolean;
  upgradeUrgent?: boolean;
}

const Header = ({ 
  teamName, 
  teamId, 
  onLogout, 
  onTeamClick,
  showTeamSwitcher = true,
  onUpgrade,
  showUpgrade = false,
  upgradeUrgent = false
}: HeaderProps = {}) => {
  const navigate = useNavigate();
  
  return (
    <header className="bg-white/60 backdrop-blur-md border-b border-white/20 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <img 
              src="https://cfcpcmswpbygvdbyrivt.supabase.co/storage/v1/object/sign/images/syncsimplelogo-color.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9hMThjNGE0NC1hYzkzLTQwYTYtYTk5Yy0wY2NlYTdiZDZmM2UiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvc3luY3NpbXBsZWxvZ28tY29sb3IucG5nIiwiaWF0IjoxNzUyMzU0ODIxLCJleHAiOjE5MTAwMzQ4MjF9.geBqZOj9PK5jz1kE7zDLnYvzNqERaC7pVl9btt28T_I" 
              alt="SyncSimple Logo" 
              className="h-8 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/dashboard')}
            />
          </div>
          
          <nav className="flex items-center space-x-4">
            {/* Team Switcher - only show if we have team info */}
            {showTeamSwitcher && (teamId || teamName) && (
              <TeamSwitcher 
                currentTeamId={teamId}
                currentTeamName={teamName}
              />
            )}
            
            {/* Upgrade Button - show if trial is active or expired */}
            {showUpgrade && onUpgrade && (
              <button 
                onClick={onUpgrade}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 hover:shadow-md transform hover:scale-105 ${
                  upgradeUrgent 
                    ? 'bg-red-500 border-2 border-red-600 text-white hover:bg-red-600 animate-pulse' 
                    : 'bg-gradient-to-r from-amber-400 to-amber-500 border border-amber-600 text-white hover:from-amber-500 hover:to-amber-600'
                }`}
              >
                {upgradeUrgent ? (
                  <>
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-bold">Trial Expired - Upgrade Now</span>
                  </>
                ) : (
                  <>
                    <Crown className="h-4 w-4" />
                    <span>Upgrade Plan</span>
                  </>
                )}
              </button>
            )}
            
            {/* Team Settings Button - only show if onTeamClick is provided */}
            {onTeamClick && (
              <button 
                onClick={onTeamClick}
                className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/40 border border-white/30 text-slate-600 hover:bg-white/60 hover:text-slate-700 transition-all duration-300 hover:shadow-sm"
              >
                <Users className="h-4 w-4" />
                <span className="font-medium text-sm">Settings</span>
              </button>
            )}
            
            {onLogout && (
              <button 
                onClick={onLogout}
                className="flex items-center space-x-2 px-4 py-2 rounded-full bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-300 hover:shadow-sm"
              >
                <span className="font-medium text-sm">Logout</span>
              </button>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;